/**
 * IdentityPill — global signed-in chip + sign-out menu.
 *
 * Renders in the Header next to the nav. Two states:
 *   • signed in   → "Jane Smith ↓" with a dropdown showing email + sign out
 *   • signed out  → "Sign in" CTA opening the LightSignIn modal
 *
 * Sign-out clears identity AND navigates to `/` (the description page)
 * — the user loses access to every other route until they sign in
 * again.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getIdentity, clearIdentity } from "../lib/learnerIdentity";
import LightSignIn from "./LightSignIn";

const ACCENT = "#6B8F71";
const ACCENT_OK_GREEN = "#22c55e";

export default function IdentityPill() {
  const [identity, setIdentity] = useState(() => getIdentity());
  const [linkedId, setLinkedId] = useState(() => localStorage.getItem("mc_learner_id") || "");
  const [open, setOpen] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Cross-tab + same-tab sync
  useEffect(() => {
    const onStorage = () => {
      setIdentity(getIdentity());
      setLinkedId(localStorage.getItem("mc_learner_id") || "");
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("maldek-identity-change", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("maldek-identity-change", onStorage);
    };
  }, []);

  const handleSignOut = () => {
    if (!window.confirm("Sign out? You'll lose access to learner modules and end up on the overview page.")) return;
    clearIdentity();
    setIdentity(null);
    setOpen(false);
    nav("/", { replace: true });
  };

  if (!identity) {
    return (
      <>
        {showSignIn && (
          <LightSignIn
            forceOpen
            onSignedIn={(id) => { setIdentity(id); setShowSignIn(false); }}
          />
        )}
        <button
          type="button"
          onClick={() => setShowSignIn(true)}
          data-testid="identity-pill-signin"
          className="px-3 py-1.5 rounded-full border border-cyan-300/40 text-cyan-100 hover:text-white hover:border-cyan-200/70 font-mono text-[10px] uppercase tracking-widest transition-colors"
        >
          Sign in
        </button>
      </>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="identity-pill">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="identity-pill-button"
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 text-white/85 hover:text-white hover:border-white/35 font-mono text-[10px] uppercase tracking-widest transition-colors"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: ACCENT_OK_GREEN }} />
        {identity.display_name}
        {linkedId && <span className="normal-case" style={{ color: ACCENT_OK_GREEN }}>· {linkedId}</span>}
        <span className="opacity-50">▾</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 rounded-xl border p-3 z-50"
          style={{
            background: "rgba(10,18,38,0.97)",
            borderColor: `${ACCENT}33`,
            boxShadow: `0 10px 40px rgba(0,0,0,0.6), 0 0 0 1px ${ACCENT}22`,
            backdropFilter: "blur(12px)",
          }}
          data-testid="identity-pill-menu"
        >
          <div className="px-2 pb-3 mb-2 border-b border-white/10">
            <p className="text-white text-sm font-medium leading-tight">{identity.display_name}</p>
            <p className="text-white/55 text-[10px] font-mono mt-0.5 break-words">{identity.email}</p>
            {linkedId ? (
              <p className="text-[10px] font-mono mt-1.5" style={{ color: ACCENT_OK_GREEN }} data-testid="identity-pill-linked-id">
                ✓ Case work credited to {localStorage.getItem("mc_learner_name") || "you"} · {linkedId}
              </p>
            ) : (
              <p className="text-white/35 text-[9px] font-mono mt-1">No learner ID linked — add yours on the Case Files page.</p>
            )}
            <p className="text-white/35 text-[9px] font-mono mt-1">ID · {identity.learner_id}</p>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            data-testid="identity-pill-signout"
            className="w-full text-left px-2 py-2 rounded-md text-sm text-red-300 hover:text-red-200 hover:bg-red-400/10 transition-colors"
          >
            ↪ Sign out
          </button>
        </div>
      )}
    </div>
  );
}
